import React, { createContext, useContext, useState, useEffect } from "react";
import { API_URLS } from "../config/environment";
import { AuthContext } from "./context";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [anonymousQuotesAssociated, setAnonymousQuotesAssociated] =
    useState(null);
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // 🔥 NUEVO ESTADO

  // Cargar usuario y token del localStorage al iniciar
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsInitializing(true); // 🔥 MARCAR COMO INICIALIZANDO
        const savedUser = localStorage.getItem("tattersall-user");
        const savedToken = localStorage.getItem("tattersall-token");

        if (savedUser && savedToken) {
          setUser(JSON.parse(savedUser));
          setToken(savedToken);

          // Validar token inmediatamente al cargar la app
          const isValid = await validateToken(savedToken);
          if (!isValid) {
            // Token inválido, cerrar sesión
            logout();
          }
        }
      } catch (error) {
        console.error("Error inicializando autenticación:", error);
        setUser(null);
        setToken(null);
        localStorage.removeItem("tattersall-user");
        localStorage.removeItem("tattersall-token");
      } finally {
        setIsInitializing(false); // 🔥 MARCAR COMO TERMINADO
      }
    };

    initializeAuth();
  }, []);

  // Validar token periódicamente cada 15 minutos
  useEffect(() => {
    let intervalId;

    if (token) {
      intervalId = setInterval(async () => {
        const isValid = await validateToken(token);
        if (!isValid) {
          logout();
        }
      }, 15 * 60 * 1000); // 15 minutos
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [token]);

  // Escuchar eventos de token expirado desde los servicios
  useEffect(() => {
    const handleTokenExpired = (event) => {
      console.warn("🔐 Token expirado detectado por servicio, cerrando sesión");
      logout();
    };

    window.addEventListener("token-expired", handleTokenExpired);

    return () => {
      window.removeEventListener("token-expired", handleTokenExpired);
    };
  }, []);

  // Función para validar si el token sigue activo
  const validateToken = async (tokenToValidate) => {
    if (!tokenToValidate) return false;

    try {
      setIsValidatingToken(true);

      // Hacer una llamada a un endpoint que requiera autenticación
      // Usamos el endpoint de cotizaciones con límite 1 para ser eficiente
      const response = await fetch(`${API_URLS.QUOTES}?limit=1`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${tokenToValidate}`,
          "Content-Type": "application/json",
        },
      });

      setIsValidatingToken(false);

      // Si la respuesta es 401 o 403, el token es inválido
      if (response.status === 401 || response.status === 403) {
        console.warn("🔐 Token expirado o inválido");
        return false;
      }

      // Si hay otros errores de red, mantener la sesión
      if (!response.ok) {
        console.warn(
          "⚠️ Error validando token, manteniendo sesión:",
          response.status
        );
        return true; // No cerrar sesión por errores de red
      }

      return true;
    } catch (error) {
      setIsValidatingToken(false);
      console.warn(
        "⚠️ Error validando token, manteniendo sesión:",
        error.message
      );
      return true; // No cerrar sesión por errores de red
    }
  };

  // Función para validar token manualmente
  const checkTokenValidity = async () => {
    if (!token) return false;
    return await validateToken(token);
  };

  // Función para aplanar los datos del login
  const parseUserData = (data) => {
    if (!data || !data.user) return null;
    const personId = data.user.personId;
    const entityId = data.objects?.people?.[personId]?.entityId;
    return {
      ...data.user,
      ...data.objects?.people?.[personId],
      ...(data.objects?.entities?.[entityId] || {}), // aplanar entity directamente
      globals: data.globals || {},
      token: data.token,
    };
  };

  // Login real
  const login = async ({ email, password }) => {
    setLoading(true);
    setError(null);
    setAnonymousQuotesAssociated(null); // Reset estado de asociación

    try {
      const res = await fetch(API_URLS.LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          source: "venta-usados", // Nuevo parámetro requerido
        }),
      });
      const data = await res.json();
      if (data.token && data.user) {
        const parsedUser = parseUserData(data);
        setUser(parsedUser);
        setToken(data.token);
        localStorage.setItem("tattersall-user", JSON.stringify(parsedUser));
        localStorage.setItem("tattersall-token", data.token);
        setError(null);

        // NUEVA FUNCIONALIDAD: La auto-asociación sucede automáticamente en el backend
        // Solo necesitamos mostrar una notificación al usuario si hubo asociaciones
        setTimeout(async () => {
          try {
            await checkForAssociatedQuotes(parsedUser);
          } catch (error) {
            console.warn(
              "⚠️ Error verificando asociaciones automáticas:",
              error
            );
            // No fallar el login por esto
          }
        }, 2000); // Dar tiempo para que el backend procese la auto-asociación

        setLoading(false);
        return { success: true };
      } else {
        setUser(null);
        setToken(null);
        localStorage.removeItem("tattersall-user");
        localStorage.removeItem("tattersall-token");
        setError(data.error || "missing credentials");
        setLoading(false);
        return { success: false, error: data.error || "missing credentials" };
      }
    } catch (error) {
      setUser(null);
      setToken(null);
      setError("network error");
      setLoading(false);
      return { success: false, error: "network error " + error.message };
    }
  };

  // Función para verificar si hubo cotizaciones asociadas automáticamente
  const checkForAssociatedQuotes = async (userData) => {
    try {
      // Importar funciones de tracking
      const { checkAssociationPotential, clearAnonymousQuotes } = await import(
        "../utils/anonymousTracker"
      );

      // Verificar si había cotizaciones anónimas locales antes del login
      const potential = checkAssociationPotential(
        userData.email,
        userData.taxNumber
      );

      if (potential.hasMatches) {
        // Si había potencial para asociación, verificar si el backend las asoció
        const { API_BASE_URL } = await import("../services/productsService");

        // Obtener cotizaciones del usuario para ver si aumentaron
        const response = await fetch(
          `${API_BASE_URL}/quotes?own_only=true&limit=100`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userData.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const result = await response.json();

          // Contar cotizaciones que fueron asociadas recientemente (últimos 5 minutos)
          const recentlyAssociated =
            result.data?.filter((quote) => {
              if (!quote.associated_at) return false;
              const associatedTime = new Date(quote.associated_at);
              const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
              return associatedTime > fiveMinutesAgo;
            }) || [];

          if (recentlyAssociated.length > 0) {
            setAnonymousQuotesAssociated({
              success: true,
              count: recentlyAssociated.length,
              message: `¡Encontramos ${recentlyAssociated.length} cotización${
                recentlyAssociated.length > 1 ? "es" : ""
              } anterior${recentlyAssociated.length > 1 ? "es" : ""} y la${
                recentlyAssociated.length > 1 ? "s" : ""
              } asociamos a tu cuenta!`,
            });

            // Limpiar datos locales ya que fueron asociados
            clearAnonymousQuotes();

            console.log(
              `✅ Auto-asociación detectada: ${recentlyAssociated.length} cotizaciones`
            );
          } else {
            // No se detectaron asociaciones recientes, pero limpiar datos locales de todas formas
            clearAnonymousQuotes();
          }
        }
      }
    } catch (error) {
      console.warn("⚠️ Error verificando asociaciones automáticas:", error);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setAnonymousQuotesAssociated(null);
    localStorage.removeItem("tattersall-user");
    localStorage.removeItem("tattersall-token");
  };

  // Función para limpiar el mensaje de asociación
  const clearAnonymousQuotesMessage = () => {
    setAnonymousQuotesAssociated(null);
  };

  const isAdmin = () => user?.roleCode === "ADM" && user?.areaId === 1;
  const isLoggedIn = () => !!user && !!token;

  const value = {
    user,
    token,
    loading,
    error,
    anonymousQuotesAssociated,
    isValidatingToken,
    isInitializing,
    login,
    logout,
    clearAnonymousQuotesMessage,
    isAdmin,
    isLoggedIn,
    checkTokenValidity,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
